import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {tabWrapperGap} from './tabsWrapper';

/*
 * Props.
 */

interface TabProps {
  className?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  maxWidth?: string | number;
  label: React.ReactNode;
  isOverflowDisabled?: boolean;
  onClick?: ClickEventHandler;
  interactionId?: string;
}

/*
 * Style.
 */

const StyledSelectedBorderDiv = styled.div`
  position: absolute;
  background: ${(p) => p.theme.greys.black};
  bottom: 4px;
  left: 0;
  right: 0;
  height: 4px;
  border-top-left-radius: 100px;
  border-top-right-radius: 100px;
`;

interface StyledTabContainerDivProps {
  $maxWidth?: string | number;
  $isOverflowDisabled?: boolean;
}

const StyledTabContainerDiv = styled.div<StyledTabContainerDivProps>`
  display: flex;
  flex-flow: column;
  overflow: hidden;

  ${(p) =>
    p.$isOverflowDisabled &&
    css`
      overflow: visible;
    `}
  ${(p) =>
    p.$maxWidth &&
    css`
      /* The tabWrapperGap / 2 is needed to account for the gap when we have a max width */
      max-width: calc(
        ${typeof p.$maxWidth === 'string' ? p.$maxWidth : `${p.$maxWidth}px`} - ${tabWrapperGap / 2}px
      );
    `}
`;

interface StyledTabContentDivProps {
  $isSelected: boolean;
  $isHovering: boolean;
}
const StyledTabContentDiv = styled.div<StyledTabContentDivProps>`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade60};

  ${(p) =>
    p.$isSelected &&
    css`
      color: ${p.theme.greys.black};
    `}
  ${(p) =>
    p.$isHovering &&
    css`
      color: ${p.theme.greys.black};
    `}
`;

const StyledSelectedBorderContainerDiv = styled.div`
  position: relative;
  height: 20px;
`;

/*
 * Component.
 */

export const Tab: FC<TabProps> = (props) => {
  const {children, className, isSelected, maxWidth, label, isDisabled, isOverflowDisabled, onClick} = props;
  const [isHovering, setIsHovering] = useState(false);

  return (
    <StyledTabContainerDiv
      className={className}
      $maxWidth={maxWidth}
      $isOverflowDisabled={isOverflowDisabled}
      onClick={(e) => {
        if (isDisabled || !onClick) {
          return;
        }
        onClick(e);
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseOver={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      {...buildInteractionIdProps(props.interactionId)}
    >
      <StyledTabContentDiv $isSelected={Boolean(isSelected && !isDisabled)} $isHovering={isHovering}>
        <TooltipCoordinator render={() => label}>
          <TooltipOverflowCondition>{children}</TooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledTabContentDiv>
      <StyledSelectedBorderContainerDiv>
        {isSelected && !isDisabled && <StyledSelectedBorderDiv />}
      </StyledSelectedBorderContainerDiv>
    </StyledTabContainerDiv>
  );
};
